import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import Avatar from '@mui/material/Avatar'
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import LoginProblemLinks from './LoginProblemLinks'
import { formatDeviseErrors, splitFieldErrors, fieldErrorText } from './deviseErrors'
import { deviseFetch, showFailedRequest } from './deviseRequest'
import useSubmitting from '../../hooks/useSubmitting'
import {
  setFlashNotice,
  setFlashAlert,
  clearFlashNotice,
  clearFlashAlert,
} from '../../redux/features/ui/uiSlice'
import {
  selectAuthToken,
  setAuthToken,
  fetchUserAsync,
} from '../../redux/features/user/userSlice'

// The page the password reset email links to (/app/user/password/edit): choose
// a new password. Devise signs the user in once it is saved, so the screen then
// loads the user and goes straight into the app.
export default function ChooseNewPassword() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const storedAuthToken = useSelector(selectAuthToken)
  const csrfToken = storedAuthToken || document.querySelector('meta[name=csrf-token]')?.content || ''
  const resetToken = new URLSearchParams(window.location.search)
    .get('reset_password_token') || ''
  const missingTokenHandled = useRef(false)
  const [errors, setErrors] = useState({})
  const [submitting, submit] = useSubmitting()

  useEffect(() => {
    if (!storedAuthToken) {
      const documentAuthToken = document.querySelector('meta[name=csrf-token]')?.content
      if (documentAuthToken) {
        dispatch(setAuthToken(documentAuthToken))
      }
    }
  }, [dispatch, storedAuthToken])

  // A link without a token can't work: ask for a new one.
  useEffect(() => {
    if (!resetToken && !missingTokenHandled.current) {
      missingTokenHandled.current = true
      dispatch(setFlashAlert(
        'The password reset link was incomplete. Request a new one.'
      ))
      navigate('/app/reset_password')
    }
  }, [])

  const handleSubmit = (evt) => {
    evt.preventDefault()
    setErrors({})
    dispatch(clearFlashNotice())
    dispatch(clearFlashAlert())

    const data = new FormData(evt.currentTarget)
    if (csrfToken) {
      data.set('authenticity_token', csrfToken)
    }

    submit(async () => {
      const result = await deviseFetch(dispatch, "/user/password.json", {
        method: 'PUT',
        body: data,
        headers: {
          'Accept': 'application/json',
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        }
      })

      if (result.failure) {
        showFailedRequest(dispatch, result)
        return
      }
      const { response, json: json_data } = result

      if (response.status === 204) {
        // Devise has signed the user in. Load them and their pages so the app
        // switches to the signed-in routes, then go in.
        await dispatch(fetchUserAsync())
        dispatch(setFlashNotice('Your password was changed. You are signed in.'))
        navigate('/app/pages')
        return
      }

      if (response.status === 422) {
        // An invalid or used link can't be fixed on this page: ask for a new one.
        if (json_data?.errors?.reset_password_token) {
          dispatch(setFlashAlert(
            'The password reset link was invalid or has expired. Request a new one.'
          ))
          navigate('/app/reset_password')
          return
        }

        // Errors beside the fields, with a summary in the flash.
        const { fieldErrors, flashMessage } = splitFieldErrors(
          json_data, ['password', 'password_confirmation'],
          'the password from being changed'
        )
        setErrors(fieldErrors)
        dispatch(setFlashAlert(flashMessage))
        return
      }

      dispatch(setFlashAlert(formatDeviseErrors(json_data)))
    })
  }

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 9,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar>

        <Typography component='h1' variant='h5'>
          Choose a new password
        </Typography>
        <Box
          component="form"
          sx={{ mt: 1, width: '100%', maxWidth: 400 }}
          onSubmit={handleSubmit}
          noValidate
        >
          <input type="hidden" name="user[reset_password_token]" value={resetToken} />
          <TextField
            margin='normal'
            required
            fullWidth
            id='user[password]'
            label='New password'
            name='user[password]'
            type='password'
            autoComplete='new-password'
            error={Boolean(errors.password)}
            helperText={fieldErrorText(errors, 'password')}
            autoFocus
          />
          <TextField
            margin='normal'
            required
            fullWidth
            id='user[password_confirmation]'
            label='Confirm new password'
            name='user[password_confirmation]'
            type='password'
            autoComplete='new-password'
            error={Boolean(errors.password_confirmation)}
            helperText={fieldErrorText(errors, 'password_confirmation')}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            loading={submitting}
            sx={{ mt: 3, mb: 2 }}
          >
            Change my password
          </Button>
          <LoginProblemLinks />
        </Box>
      </Box>
    </Container>
  )
}
